import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { GUIDED_TOUR, TourStep, tourForRole } from './tourSteps';
import { getDemoRole } from '@/lib/demoRole';
import { useAuth } from '@/hooks/useAuth';

const SEEN_KEY_BASE = 'sellaris.tour.seen.v1';
const seenKeyFor = (userId?: string | null) =>
  userId ? `${SEEN_KEY_BASE}:${userId}` : SEEN_KEY_BASE;

interface TourContextValue {
  running: boolean;
  index: number;
  total: number;
  step: TourStep | null;
  start: () => void;
  next: () => void;
  prev: () => void;
  end: () => void;
  hasSeen: boolean;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    // Safe no-op so components can render outside the provider (e.g. tests).
    return {
      running: false,
      index: 0,
      total: GUIDED_TOUR.length,
      step: null,
      start: () => {},
      next: () => {},
      prev: () => {},
      end: () => {},
      hasSeen: true,
    } as TourContextValue;
  }
  return ctx;
}

export function TourProvider({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const seenKey = seenKeyFor(user?.id);
  const [running, setRunning] = useState(false);
  const [index, setIndex] = useState(0);
  const [hasSeen, setHasSeen] = useState(false);

  // Seen state is per user so one person finishing the tour doesn't replay it
  // for the next account signed in on the same browser.
  useEffect(() => {
    try {
      setHasSeen(localStorage.getItem(seenKeyFor(user?.id)) === '1');
    } catch {
      setHasSeen(false);
    }
  }, [user?.id]);
  const returnToRef = useRef<string>('/dashboard');
  const steps = useMemo(() => tourForRole(getDemoRole()), [running, location.pathname]);
  const sessionRouteRef = useRef<string | null>(null);

  const step = running ? steps[index] ?? null : null;

  const start = useCallback(() => {
    returnToRef.current = `${location.pathname}${location.search}`;
    setIndex(0);
    setRunning(true);
  }, [location.pathname, location.search]);

  const end = useCallback(() => {
    setRunning(false);
    setIndex(0);
    try {
      localStorage.setItem(seenKey, '1');
    } catch {
      /* ignore */
    }
    setHasSeen(true);
    const back = returnToRef.current || '/dashboard';
    navigate(back, { replace: true });
  }, [navigate, seenKey]);

  const next = useCallback(() => {
    setIndex(i => {
      if (i >= steps.length - 1) {
        // finish on the next tick so state updates settle first
        setTimeout(() => end(), 0);
        return i;
      }
      return i + 1;
    });
  }, [end, steps.length]);

  const prev = useCallback(() => {
    setIndex(i => Math.max(0, i - 1));
  }, []);

  // Resolve a real session route once, lazily.
  const resolveSessionRoute = useCallback(async () => {
    if (sessionRouteRef.current) return sessionRouteRef.current;
    const { data } = await supabase
      .from('practice_sessions')
      .select('id, started_at, overall_score')
      .not('overall_score', 'is', null)
      .order('started_at', { ascending: false })
      .limit(1);

    const id = data?.[0]?.id;
    sessionRouteRef.current = id ? `/sessions/${id}` : null;
    return sessionRouteRef.current;
  }, []);

  // Drive navigation for the active step.
  useEffect(() => {
    if (!running || !step) return;
    let cancelled = false;

    const go = async () => {
      let target = step.route ?? null;
      if (step.resolveRoute === 'latestSession') {
        target = (await resolveSessionRoute()) ?? '/dashboard?tab=recent-sessions';
      }
      if (cancelled || !target) return;
      const current = `${window.location.pathname}${window.location.search}`;
      if (current !== target) navigate(target);
    };

    go();
    return () => {
      cancelled = true;
    };
  }, [running, step, navigate, resolveSessionRoute]);

  // Auto-start is handled by <TourLauncher /> and only for demo visitors.
  // Signed-in real accounts never get the walkthrough pushed on them.

  // Keyboard shortcuts
  useEffect(() => {
    if (!running) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') end();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [running, end, next, prev]);

  const value = useMemo(
    () => ({
      running,
      index,
      total: steps.length,
      step,
      start,
      next,
      prev,
      end,
      hasSeen,
    }),
    [running, index, step, start, next, prev, end, hasSeen, steps.length]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
